import { useCallback, useEffect, useId, useMemo, useState } from "react";
import {
  Send, Loader2, WifiOff, CheckCircle2, AlertCircle, Plus, CalendarClock,
  Building2, Trash2,
} from "lucide-react";
import PageHeader from "@/components/dashboard/PageHeader";
import Button from "@/components/ui/Button";
import Switch from "@/components/ui/Switch";
import RecipientsField from "@/components/dashboard/RecipientsField";
import ConfirmDialog from "@/components/dashboard/ConfirmDialog";
import { cn } from "@/lib/cn";
import { useApiData } from "@/hooks/useApiData";
import LoadError from "@/components/dashboard/LoadError";
import {
  getPlatformUsageReport, createPlatformUsageSchedule, updatePlatformUsageSchedule,
  deletePlatformUsageSchedule, sendPlatformUsageSchedule,
} from "@/api/admin";
import { fieldErrors } from "@/api/client";
import { commitPending, validateRecipientList } from "@/lib/recipients";

// Fallback only for the unreachable-backend path. The frequency list normally
// comes FROM the API (`data.frequencies`) so the picker can never offer a value
// the server would reject.
const FALLBACK_FREQUENCIES = [
  { value: "daily", label: "Daily", days: 1 },
  { value: "weekly", label: "Weekly", days: 7 },
  { value: "monthly", label: "Monthly", days: 30 },
  { value: "annually", label: "Annually", days: 365 },
];

const DEMO = {
  schedules: [],
  frequencies: FALLBACK_FREQUENCIES,
  preview_frequency: "weekly",
  preview: { period_days: 7, period_start: null, period_end: null, total_runs: 512, pass_rate: 79.5, bugs_found: 64, active_companies: 12, total_cost_usd: 18.7, by_company: [{ company: "Acme", runs: 210, cost_usd: 7.4 }, { company: "Globex", runs: 180, cost_usd: 6.1 }] },
  email_configured: true,
};

// Module-level so the "new schedule" card receives the SAME object on every
// render. An inline literal was a fresh identity each time, which re-ran the
// card's sync effect and wiped whatever had been typed into it.
const BLANK_SCHEDULE = { enabled: false, frequency: "weekly", recipients: [] };

// Report periods are UTC instants (see reporting/usage_report.period_bounds).
// Storage, scheduling and the API all stay UTC; the only step that converts is
// DISPLAY, and it converts into the zone the backend serves as
// `display_timezone` — the very same zone the emailed report stamps itself in
// (reporting.usage_report.display_tz). That is the point: the page and the
// inbox describe one instant one way. The zone is always printed, and an
// unknown one falls back to UTC rather than throwing.
const dayOpts = (timeZone) => ({ day: "numeric", month: "short", year: "numeric", timeZone });
function zoneOrUtc(timeZone) {
  if (!timeZone) return "UTC";
  try {
    new Intl.DateTimeFormat("en-GB", { timeZone });
    return timeZone;
  } catch { return "UTC"; }
}
function utcDate(iso, timeZone = "UTC") {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString("en-GB", dayOpts(zoneOrUtc(timeZone)));
}
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
function utcDateTime(iso, timeZone = "UTC", label = "") {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const tz = zoneOrUtc(timeZone);
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz, day: "2-digit", month: "numeric", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: true,
  }).formatToParts(d);
  const get = (t) => (parts.find((p) => p.type === t) || {}).value || "";
  const month = MONTHS[Number(get("month")) - 1] || get("month");
  // Same shape AND the same zone label as the backend's _fmt_dt, so this page
  // and the emailed report describe one instant identically. The label comes
  // from the API because Intl calls Asia/Kolkata "GMT+5:30", not "IST".
  return `${get("day")} ${month} ${get("year")}, ${get("hour")}:${get("minute")} ${get("dayPeriod").toUpperCase()} ${label || tz}`;
}

function Toast({ type, message, onClose }) {
  if (!message) return null;
  return (
    <div className={cn("mb-5 flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm",
      type === "error" ? "border-rose-200 bg-rose-50/70 text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-200"
                       : "border-emerald-200 bg-emerald-50/70 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-200")}>
      {type === "error" ? <AlertCircle size={15} className="shrink-0" /> : <CheckCircle2 size={15} className="shrink-0" />}
      <span className="flex-1">{message}</span>
      <button type="button" onClick={onClose} className="text-xs font-medium opacity-60 hover:opacity-100">✕</button>
    </div>
  );
}

function Stat({ value, label }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-center dark:border-white/10 dark:bg-white/5">
      <div className="text-xl font-bold text-slate-800 dark:text-white">{value}</div>
      <div className="text-[0.6875rem] uppercase tracking-wider text-slate-400">{label}</div>
    </div>
  );
}

// One schedule — its own frequency, recipients, enabled state and lifecycle.
// Editing or disabling one of these never touches another: every action here
// addresses the row by its own `id`.
function ScheduleCard({
  schedule, frequencies, emailReady, onSave, onDelete, onSend, onFrequencyPreview,
  // The zone the report stamps itself in, so this card's "next" / "last sent"
  // read the same way as the email they refer to.
  displayTz = "UTC",
  displayTzLabel = "",
  // Where a failure that is NOT about the recipients field goes. Without it the
  // card had nowhere to report one and re-threw into an unhandled rejection.
  onSaveError,
}) {
  const isNew = !schedule.id;
  const toggleId = useId();
  const [enabled, setEnabled] = useState(!!schedule.enabled);
  const [frequency, setFrequency] = useState(schedule.frequency || "weekly");
  const [recipients, setRecipients] = useState(schedule.recipients || []);
  const [pending, setPending] = useState("");     // typed but not yet added
  const [fieldError, setFieldError] = useState("");
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Re-sync when the server hands back a CHANGED row (after a save or a send), so
  // the card shows stored state rather than drifting local state.
  //
  // Keyed on a signature of the saved values, not on the object's identity. Every
  // reload produces a fresh object — and picking a frequency triggers a reload,
  // because the preview follows it — so an identity dependency re-ran this on
  // each refetch and snapped the operator's half-made edit back to whatever was
  // last saved. An unchanged payload now changes no signature and resets nothing.
  const savedSig = JSON.stringify([schedule.id, !!schedule.enabled,
                                   schedule.frequency, schedule.recipients || []]);
  useEffect(() => {
    const [, e, f, r] = JSON.parse(savedSig);
    setEnabled(e);
    setFrequency(f || "weekly");
    setRecipients(r);
  }, [savedSig]);

  // A HALF-TYPED RECIPIENT COUNTS AS A CHANGE.
  //
  // This is what made Save look dead. `dirty` compared only the committed
  // fields, so typing an address into the box and pressing Save — the single
  // commonest edit on this page — left `dirty` false, which DISABLES the
  // button. The click never lands, no request is sent, and nothing explains
  // why; save() already folds the draft in via commitPending(), but it could
  // never run. Counting the draft lets the click through to that logic, which
  // either commits the address or reports what is wrong with it.
  const dirty = isNew
    || !!pending.trim()
    || enabled !== !!schedule.enabled
    || frequency !== schedule.frequency
    || JSON.stringify(recipients) !== JSON.stringify(schedule.recipients || []);

  async function save() {
    if (busy) return;
    // An address typed into the box but never "added" used to be dropped on the
    // floor by Save, so a schedule could be stored with no recipients by someone
    // who had plainly just entered one. Commit it first, and refuse if it is bad.
    const { list, error: pendingError } = commitPending(recipients, pending);
    if (pendingError) return setFieldError(pendingError);
    // A recipient is required whatever the switch says — the same rule the API
    // enforces, checked here so an invalid configuration never leaves the page
    // as a request.
    const listError = validateRecipientList(list);
    if (listError) {
      setRecipients(list);
      setPending("");
      return setFieldError(listError);
    }
    setRecipients(list);
    setPending("");
    setBusy(true);
    setFieldError("");
    try {
      await onSave(schedule.id, { enabled, frequency, recipients: list });
      setPending("");
    } catch (e) {
      // A 400 from this API carries {field, message}; put it on the field it
      // names rather than showing a bare banner.
      const fields = fieldErrors(e);
      const detail = e && e.data && e.data.detail;
      if (detail && typeof detail === "object" && detail.field === "recipients") {
        setFieldError(detail.message);
      } else if (fields.recipients) {
        setFieldError(fields.recipients);
      } else {
        // ANYTHING ELSE IS REPORTED, NOT RE-THROWN.
        //
        // `throw e` here left an async click handler with an unhandled promise
        // rejection: React renders nothing for it, so a 403, 404, 500, a proxy's
        // 502 or a blocked/CORS request (status 0) all ended as a spinner that
        // stopped and a page that said nothing — "the Save button does nothing".
        // Those are the failures a DEPLOYED app actually meets and a local one
        // never does, which is why this only ever showed up in production.
        onSaveError(e);
      }
    } finally { setBusy(false); }
  }

  async function send() {
    if (sending) return;
    if (dirty) return setFieldError("Save your changes first — Send now uses the saved recipient list.");
    setSending(true);
    try { await onSend(schedule.id); } finally { setSending(false); }
  }

  const days = (frequencies.find((f) => f.value === frequency) || {}).days;

  return (
    <div className="card p-6">
      <div className="flex items-start justify-between gap-3">
        <h3 className="flex items-center gap-2 text-base font-semibold text-slate-800 dark:text-white">
          <CalendarClock size={17} className="text-brand-500" />
          {isNew ? "New schedule" : `${(schedule.frequency || "").replace(/^./, (c) => c.toUpperCase())} report`}
        </h3>
        {!isNew && (
          <button type="button" onClick={() => setConfirmDelete(true)} title="Delete this schedule"
            aria-label={`Delete the ${schedule.frequency} schedule`}
            className="grid h-8 w-8 place-items-center rounded-lg text-slate-400 transition-colors hover:bg-rose-50 hover:text-rose-500 dark:hover:bg-rose-500/10">
            <Trash2 size={15} />
          </button>
        )}
      </div>

      {/* The row is NOT a <label> wrapping the control. It used to be, so a
          native label activation fired on every click anywhere in the row —
          the descriptive text, the padding, the whitespace — and the switch
          flipped when nobody meant to touch it. The text is now tied to the
          input by htmlFor/id instead, which keeps the click target, the
          keyboard focus and the screen-reader name intact while confining the
          activating area to the label text and the control itself. */}
      <Switch
        id={toggleId}
        className="mt-5"
        label="Automatic platform report"
        description={enabled
          ? "On — the digest is sent automatically on this schedule."
          : "Off — no automatic digest is sent."}
        checked={enabled}
        disabled={busy}
        onChange={setEnabled}
      />

      <div className="mt-4">
        <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-slate-400">Frequency</p>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {frequencies.map((f) => (
            <button key={f.value} type="button"
              aria-pressed={frequency === f.value}
              onClick={() => { setFrequency(f.value); onFrequencyPreview(f.value); }}
              className={cn("rounded-lg border px-3 py-2 text-sm font-medium",
                frequency === f.value
                  ? "border-brand-500 bg-brand-50 text-brand-700 dark:bg-brand-500/10 dark:text-brand-300"
                  : "border-slate-200 text-slate-600 hover:border-slate-300 dark:border-white/10 dark:text-slate-300")}>
              {f.label}
            </button>
          ))}
        </div>
        {days != null && (
          <p className="mt-1.5 text-xs text-slate-400">Each email covers the last {days} day{days === 1 ? "" : "s"}.</p>
        )}
      </div>

      <div className="mt-4">
        <RecipientsField
          recipients={recipients}
          onChange={setRecipients}
          pending={pending}
          onPendingChange={setPending}
          error={fieldError}
          onError={setFieldError}
          disabled={busy}
          placeholder="ops@platform.com"
        />
      </div>

      {/* When the next email goes out, straight from the server. The backend owns
          this (usage_report.next_run_at, the same call scheduler._due_by_frequency
          makes), so the page cannot promise a time the scheduler disagrees with. */}
      <div className="mt-4 rounded-xl bg-slate-50 px-4 py-3 dark:bg-white/5">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Next report</p>
        <p className="mt-0.5 text-sm font-medium text-slate-700 dark:text-slate-200">
          {dirty && !isNew ? "Save to update the schedule"
            : !enabled ? "Not scheduled — the report is switched off"
            : schedule.next_run_is_immediate ? "At the next hourly check"
            : utcDateTime(schedule.next_run_at, displayTz, displayTzLabel) || "At the next hourly check"}
        </p>
        {!isNew && (
          <p className="mt-1 text-xs text-slate-400">
            Last sent: {utcDateTime(schedule.last_sent_at, displayTz, displayTzLabel) || "never"}
          </p>
        )}
      </div>

      <div className="mt-5 flex flex-wrap items-center justify-end gap-2">
        {!isNew && (
          <Button variant="outline" size="sm" onClick={send} disabled={sending || !emailReady}>
            {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Send now
          </Button>
        )}
        <Button size="sm" onClick={save} disabled={busy || (!dirty && !isNew)}>
          {busy ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />}
          {isNew ? "Create schedule" : "Save"}
        </Button>
      </div>

      <ConfirmDialog
        open={confirmDelete}
        title="Delete this schedule?"
        body="It will stop sending immediately. Other schedules are unaffected."
        onConfirm={async () => { setConfirmDelete(false); await onDelete(schedule.id); }}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
}

export default function PlatformUsage() {
  // The preview window follows whichever frequency is selected in the form, so
  // the fetch is parameterised. `null` means "let the server choose" (the first
  // saved schedule, else the default) — used for the very first load.
  const [previewFreq, setPreviewFreq] = useState(null);
  const fetcher = useCallback(() => getPlatformUsageReport(previewFreq || undefined), [previewFreq]);
  const { data, loading, demo, error, reload } = useApiData(fetcher, DEMO);
  const [toast, setToast] = useState(null);
  const [adding, setAdding] = useState(false);

  const schedules = useMemo(() => (data && data.schedules) || [], [data]);
  const frequencies = (data && data.frequencies) || FALLBACK_FREQUENCIES;
  const preview = (data && data.preview) || (demo ? DEMO.preview : null);
  const byCompany = (preview && preview.by_company) || [];
  const emailReady = !!data && data.email_configured !== false;

  // Refetch the preview when the selected frequency changes. Without this the
  // figures were computed once from the saved config and never moved, so
  // switching to Monthly left the weekly numbers on screen under a monthly
  // heading — which is what "monthly preview data is wrong" actually was.
  useEffect(() => {
    if (previewFreq === null) return;
    reload();
  }, [previewFreq, reload]);

  // Has the response on screen caught up with the frequency now selected? The
  // API echoes back the window it actually computed (`preview_frequency`), so
  // staleness is read off the data rather than guessed from a timer — switching
  // quickly between frequencies shows the busy state instead of briefly
  // presenting one period's figures under another period's heading.
  const previewStale = previewFreq !== null && !!data
    && data.preview_frequency !== previewFreq;

  const ok = (m) => setToast({ type: "success", message: m });
  const fail = (e) => setToast({ type: "error", message: (e && e.message) || "Something went wrong." });

  async function saveSchedule(id, body) {
    if (id) await updatePlatformUsageSchedule(id, body);
    else await createPlatformUsageSchedule(body);
    setAdding(false);
    ok(id ? "Schedule saved." : "Schedule created.");
    await reload();
  }
  async function removeSchedule(id) {
    try { await deletePlatformUsageSchedule(id); ok("Schedule deleted."); await reload(); }
    catch (e) { fail(e); }
  }
  async function sendSchedule(id) {
    try {
      const res = await sendPlatformUsageSchedule(id);
      ok(`Report sent to ${res.sent} recipient${res.sent === 1 ? "" : "s"}.`);
      await reload();
    } catch (e) { fail(e); }
  }

  // Served by the API (admin_usage) from settings.report_timezone.
  const displayTz = (data && data.display_timezone) || "UTC";
  const displayTzLabel = (data && data.display_timezone_label) || "";
  const periodFrom = utcDate(preview && preview.period_start, displayTz);
  const periodTo = utcDate(preview && preview.period_end, displayTz);

  return (
    <>
      <PageHeader
        title="Platform Usage Report"
        subtitle="Aggregate usage across every tenant, emailed on the schedules you set."
        actions={<Button size="sm" onClick={() => setAdding(true)} disabled={adding || loading || !!error}>
          <Plus size={16} /> New schedule
        </Button>}
      />
      <Toast {...(toast || {})} onClose={() => setToast(null)} />

      <LoadError message={error} onRetry={reload} />

      {demo && (
        <div className="mb-5 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-2.5 text-sm text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
          <WifiOff size={15} /> Showing demo data — backend not reachable.
        </div>
      )}
      {data && !emailReady && (
        <div className="mb-5 flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-2.5 text-sm text-amber-800 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200">
          <AlertCircle size={15} /> Email (SMTP) isn&apos;t configured yet — configure it before scheduling.
        </div>
      )}

      {loading && !data ? (
        <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 size={26} className="animate-spin" /></div>
      ) : error ? null : (
        <div className="grid gap-5 lg:grid-cols-2">
          <div className="space-y-5">
            {schedules.length === 0 && !adding && (
              <div className="card p-6 text-center">
                <CalendarClock size={22} className="mx-auto text-slate-300 dark:text-white/20" />
                <p className="mt-2 text-sm font-medium text-slate-700 dark:text-slate-200">No schedules yet</p>
                <p className="mt-0.5 text-sm text-slate-400">
                  Add one per cadence you need — a weekly digest and a monthly summary can both run.
                </p>
                <Button size="sm" className="mt-4" onClick={() => setAdding(true)}><Plus size={15} /> New schedule</Button>
              </div>
            )}
            {schedules.map((s) => (
              <ScheduleCard key={s.id} schedule={s} frequencies={frequencies} emailReady={emailReady}
                onSave={saveSchedule} onDelete={removeSchedule} onSend={sendSchedule}
                onFrequencyPreview={setPreviewFreq} onSaveError={fail} displayTz={displayTz} displayTzLabel={displayTzLabel} />
            ))}
            {adding && (
              <div>
                <ScheduleCard schedule={BLANK_SCHEDULE}
                  frequencies={frequencies} emailReady={emailReady}
                  onSave={saveSchedule} onDelete={removeSchedule} onSend={sendSchedule}
                  onFrequencyPreview={setPreviewFreq} onSaveError={fail} displayTz={displayTz} displayTzLabel={displayTzLabel} />
                <div className="mt-2 text-right">
                  <button type="button" onClick={() => setAdding(false)}
                    className="text-xs font-medium text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">Cancel</button>
                </div>
              </div>
            )}
          </div>

          {/* Preview — recomputed by the server for the selected frequency. */}
          <div className="card self-start p-6">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-base font-semibold text-slate-800 dark:text-white">
                  This period{preview ? ` (${preview.period_days} day${preview.period_days === 1 ? "" : "s"})` : ""}
                </h3>
                {/* The window as real dates, from the same bounds the aggregation
                    used — "30 days" alone never said WHICH 30. */}
                <p className="mt-0.5 text-sm text-slate-400">
                  {periodFrom && periodTo ? `${periodFrom} – ${periodTo} (UTC)` : "Across all tenants — a live preview of the email."}
                </p>
              </div>
              {(loading || previewStale) && <Loader2 size={16} className="mt-1 shrink-0 animate-spin text-slate-300" />}
            </div>
            {!preview ? (
              <p className="mt-5 text-sm text-slate-400">Usage figures are unavailable right now.</p>
            ) : (
              <div className={cn("transition-opacity", previewStale && "opacity-40")}>
                <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Stat value={Number(preview.active_companies || 0).toLocaleString()} label="Companies" />
                <Stat value={Number(preview.total_runs || 0).toLocaleString()} label="Runs" />
                <Stat value={Number(preview.bugs_found || 0).toLocaleString()} label="Bugs" />
                <Stat value={`$${Number(preview.total_cost_usd || 0).toFixed(2)}`} label="AI cost" />
                </div>
              </div>
            )}
            {byCompany.length > 0 && (
              <div className="mt-5 border-t border-slate-100 pt-4 dark:border-white/10">
                <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-400"><Building2 size={13} /> Top companies</p>
                <div className="space-y-1.5">
                  {byCompany.slice(0, 6).map((c, i) => (
                    <div key={i} className="flex items-center justify-between gap-3 text-sm">
                      <span className="truncate text-slate-700 dark:text-slate-200" title={c.company}>{c.company}</span>
                      <span className="shrink-0 text-slate-400">{Number(c.runs || 0).toLocaleString()} runs · ${Number(c.cost_usd || 0).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
